import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import { formatCurrency } from '../util'
import { useAuth } from "../AuthProvider";
import { usePermissions } from "../rbac/PermissionsProvider";
import { PERMISSIONS } from "../rbac/permissions";
import ProductSelectorModal from "../components/ProductSelectorModal"; // adjust the relative path to match your project structure
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";

const EMPTY_FORM = {
  resellerName: "",
  phoneNumber: "",
  email: "",
  endClientName: "",
  location: "",
  blockedDate: "",
  status: "BLOCK",
  createdBy: "",
  products: [], // [{ lineId, productName, brandName, ecomProductId, availableColors, availableSizes, productColor, productSize, quantity, unitPrice }]
};

const ColorDot = ({ color, size = 18 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      bgcolor: color,
      border: "1px solid #d1d5db",
      display: "inline-block",
      verticalAlign: "middle",
      mr: 1,
      boxShadow: 1,
    }}
  />
);

// Pastel palette used to visually band consecutive rows that belong to the
// same product, so a lead with several color/size variants of one item
// doesn't look like an undifferentiated flat list.
const GROUP_PALETTE = ["#eef4ff", "#fdf1f6", "#eefaf1", "#fff7ea", "#f3eefc", "#eafbfc"];

// ── status metadata drives chip color, icon, dropdown options and analytics cards ──
const STATUS_META = {
  BLOCK: { label: "Block", color: "warning", icon: LockIcon },
  UNBLOCK: { label: "Unblock", color: "default", icon: LockOpenIcon },
  LOST: { label: "Lost", color: "error", icon: CancelIcon },
  WON: { label: "Won", color: "success", icon: CheckCircleIcon },
  ON_HOLD: { label: "On Hold", color: "info", icon: PauseCircleIcon },
};
const STATUS_ORDER = ["BLOCK", "UNBLOCK", "ON_HOLD", "WON", "LOST"];

const toDateInputValue = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val.slice(0, 10);
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

// "#000000, #000080" -> ["#000000", "#000080"]
const splitOptions = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

let lineIdCounter = 0;
const nextLineId = () => `line-${Date.now()}-${lineIdCounter++}`;

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.BLOCK;
  const Icon = meta.icon;
  return <Chip size="small" icon={<Icon fontSize="small" />} label={meta.label} color={meta.color} variant="filled" />;
};

const isValidPhone = (phone) => {
  if (!phone?.trim()) return true; // optional field
  return /^[6-9]\d{9}$/.test(phone.trim());
};

const isValidEmail = (email) => {
  if (!email?.trim()) return true; // optional field
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

const LeadBlockManagement = () => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];
  const { username } = useAuth();
  const { can } = usePermissions();


  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [viewOpen, setViewOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null);

  // ── analytics ──
  const [analytics, setAnalytics] = useState({ total: 0, counts: {}, expiringSoon: 0 });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // ── search / filter / sort ──
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // Blocked Until column
  const debounceRef = useRef(null);

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const sendAuthenticatedRequest = async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204) return {};
    const text = await res.text();
    if (!res.ok) throw new Error(text || "API failed");
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return text;
    }
  };

  const isAdmin = can(PERMISSIONS.LEAD_DELETE);

  const buildLeadsUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (statusFilter) params.set("status", statusFilter);
    params.set("sortDir", sortDir);
    return `${urlconfig.backend_api_base_url}/api/v2/leads?${params.toString()}`;
  }, [search, statusFilter, sortDir]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sendAuthenticatedRequest(buildLeadsUrl(), "GET");
      const sortedLeads = [...data].sort((a, b) => {
        if (sortDir === "asc") {
          return new Date(a.blockedDate) - new Date(b.blockedDate);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setLeads(sortedLeads || []);
    } catch (err) {
      showSnackbar("Failed to fetch leads", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildLeadsUrl]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const data = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/leads/analytics`, "GET");
      setAnalytics(data || { total: 0, counts: {}, expiringSoon: 0 });
    } catch (err) {
      // Non-fatal — table still works without analytics.
      console.error(err);
    } finally {
      setAnalyticsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!account) return;
    fetchLeads();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, buildLeadsUrl]);

  // Debounced search — refetches 400ms after the user stops typing.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleOpenCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setOpenModal(true);
  };

  // Supports both the new `lead.products` array shape and the old flat single-product
  // shape, so existing leads created before this change still open cleanly for editing.
  const handleOpenEdit = async (lead) => {
    let products = [];
    if (Array.isArray(lead.products) && lead.products.length > 0) {
      products = lead.products.map((p) => ({
        lineId: nextLineId(),
        productName: p.productName || "",
        brandName: p.brandName || "",
        ecomProductId: p.ecomProductId ?? null,
        availableColors: splitOptions(p.availableColors || p.colors),
        availableSizes: splitOptions(p.availableSizes || p.sizes),
        productColor: p.productColor || "",
        productSize: p.productSize || "",
        quantity: p.quantity?.toString() || "",
        unitPrice: p.unitPrice?.toString() || "",
      }));
    } else if (lead.productName) {
      products = [
        {
          lineId: nextLineId(),
          productName: lead.productName || "",
          brandName: lead.brandName || "",
          ecomProductId: lead.ecomProductId ?? null,
          availableColors: splitOptions(lead.productColor),
          availableSizes: splitOptions(lead.productSize),
          productColor: lead.productColor || "",
          productSize: lead.productSize || "",
          quantity: lead.quantity?.toString() || "",
          unitPrice: lead.unitPrice?.toString() || "",
        },
      ];
    }

    setForm({
      resellerName: lead.resellerName || "",
      phoneNumber: lead.phoneNumber || "",
      email: lead.email || "",
      endClientName: lead.endClientName || "",
      location: lead.location || "",
      blockedDate: toDateInputValue(lead.blockedDate),
      status: lead.status || "BLOCK",
      products,
    });
    setEditingId(lead.id);
    setOpenModal(true);

    // Saved lead products only ever store the CHOSEN color/size, never the
    // full catalog list — so without this, editing (and duplicating a line)
    // forces free-text entry and invites typos like "tte" instead of "Teal".
    // Backfill the real options per distinct product via its ecomProductId.
    const idsNeedingOptions = [
      ...new Set(
        products
          .filter((p) => p.ecomProductId != null && p.availableColors.length === 0 && p.availableSizes.length === 0)
          .map((p) => p.ecomProductId)
      ),
    ];
    if (idsNeedingOptions.length === 0) return;

    try {
      const results = await Promise.all(
        idsNeedingOptions.map((id) =>
          sendAuthenticatedRequest(
            `${urlconfig.backend_api_base_url}/api/v2/smart-search/product/${id}`,
            "GET"
          ).catch(() => null)
        )
      );

      const optionsById = {};
      idsNeedingOptions.forEach((id, idx) => {
        const match = results[idx];
        if (match) {
          optionsById[id] = {
            availableColors: splitOptions(match.colors),
            availableSizes: splitOptions(match.sizes),
          };
        }
      });
      if (Object.keys(optionsById).length === 0) return;

      setForm((prev) => ({
        ...prev,
        products: prev.products.map((p) =>
          p.ecomProductId != null && optionsById[p.ecomProductId] ? { ...p, ...optionsById[p.ecomProductId] } : p
        ),
      }));
    } catch (e) {
      // Non-fatal — fields just stay as free text for this edit session.
    }
  };

  const handleOpenView = (lead) => {
    setSelectedLead(lead);
    setViewOpen(true);
  };

  const groupedProducts = React.useMemo(() => {
    if (!selectedLead?.products) return [];

    const map = {};

    selectedLead.products.forEach((p) => {
      const key = p.ecomProductId || p.productName;

      if (!map[key]) {
        map[key] = {
          productName: p.productName,
          brandName: p.brandName,
          variants: [],
        };
      }

      map[key].variants.push(p);
    });

    return Object.values(map);
  }, [selectedLead]);

  // Assigns each distinct product in the form a pastel background so every
  // variant line of that product is visually banded together, even when
  // products were added out of order.
  const productGroupColor = React.useMemo(() => {
    const map = {};
    let idx = 0;
    form.products.forEach((p) => {
      const key = p.ecomProductId ?? p.productName;
      if (!(key in map)) {
        map[key] = GROUP_PALETTE[idx % GROUP_PALETTE.length];
        idx += 1;
      }
    });
    return map;
  }, [form.products]);

  const handleDuplicateLine = (lineId) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.flatMap((p) => {
        if (p.lineId !== lineId) return [p];

        return [
          p,
          {
            ...p,
            lineId: nextLineId(),
            // Force a re-pick so the clone can't silently save as an exact
            // duplicate of the row it came from.
            productSize: "",
            quantity: "1",
          },
        ];
      }),
    }));
  };

  const handleClose = () => {
    setOpenModal(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const isFormValid = () =>
    form.resellerName.trim() &&
    form.endClientName.trim() &&
    form.blockedDate &&
    form.products.length > 0 &&
    form.products.every((p) => p.productName.trim() && p.quantity) &&
    isValidPhone(form.phoneNumber) &&
    isValidEmail(form.email);

  // Modal is single-select — fires once per pick. Called each time the user opens
  // "Add Product" and clicks a catalog item; a lead ends up with multiple products
  // by the user repeating that action.
  const handleCatalogSelect = (product) => {
    if (!product) return;

    setForm((prev) => {
      const availableColors = splitOptions(product.colors);
      const availableSizes = splitOptions(product.sizes);

      const newLine = {
        lineId: nextLineId(),
        productName: product.productName || "",
        brandName: product.brandName || "",
        ecomProductId: product.ecomProductId ?? null,
        availableColors,
        availableSizes,
        productColor: availableColors.length === 1 ? availableColors[0] : "",
        productSize: availableSizes.length === 1 ? availableSizes[0] : "",
        quantity: "1",
        unitPrice:
          product.ecomMrp != null
            ? product.ecomMrp.toString()
            : "",
      };

      return {
        ...prev,
        products: [...prev.products, newLine],
      };
    });

    setProductModalOpen(false);
  };

  const isDuplicateVariant = (products, currentLineId, line) => {
    return products.some((p) => {
      if (p.lineId === currentLineId) return false;

      return (
        p.ecomProductId === line.ecomProductId &&
        p.productColor === line.productColor &&
        p.productSize === line.productSize
      );
    });
  };

  // Sizes of this same product+color already used on OTHER lines — used both
  // to grey out taken sizes in the dropdown and to show a plain-language
  // hint under it.
  const getTakenSizes = (products, currentLineId, ecomProductId, color) =>
    products
      .filter(
        (x) =>
          x.lineId !== currentLineId &&
          x.ecomProductId === ecomProductId &&
          x.productColor === color &&
          x.productSize
      )
      .map((x) => x.productSize);

  // How many OTHER lines already use this exact color for this product —
  // shown as a small count badge next to each color option so it's obvious
  // at a glance which colors already have variants added.
  const getColorUsageCount = (products, currentLineId, ecomProductId, color) =>
    products.filter(
      (x) => x.lineId !== currentLineId && x.ecomProductId === ecomProductId && x.productColor === color
    ).length;

  const handleLineChange = (lineId, field, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((p) => {
        if (p.lineId !== lineId) return p;

        const updated = {
          ...p,
          [field]: value,
        };

        // Changing color invalidates whatever size was picked for the old
        // color, since size availability/taken-state is per-color.
        if (field === "productColor") {
          updated.productSize = "";
        }

        return updated;
      }),
    }));
  };

  const handleRemoveLine = (lineId) => {
    setForm((prev) => ({ ...prev, products: prev.products.filter((p) => p.lineId !== lineId) }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    for (const line of form.products) {
      if (isDuplicateVariant(form.products, line.lineId, line)) {
        showSnackbar(
          `Duplicate variant found for ${line.productName} (${line.productColor || "no color"} / ${line.productSize || "no size"})`,
          "error"
        );
        setSubmitting(false);
        return;
      }
    }
    try {
      const payload = {
        resellerName: form.resellerName,
        phoneNumber: form.phoneNumber,
        email: form.email,
        endClientName: form.endClientName,
        location: form.location,
        blockedDate: form.blockedDate,
        status: form.status,
        createdBy: username || "",
        products: form.products.map((p) => ({
          productName: p.productName,
          brandName: p.brandName,
          ecomProductId: p.ecomProductId,
          productColor: p.productColor,
          productSize: p.productSize,
          quantity: Number(p.quantity),
          unitPrice: p.unitPrice ? Number(p.unitPrice) : null,
        })),
      };

      if (editingId) {
        await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/leads/${editingId}`, "PUT", payload);
        showSnackbar("Lead updated successfully");
      } else {
        await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/leads`, "POST", payload);
        showSnackbar("Lead created successfully");
      }
      handleClose();
      fetchLeads();
      fetchAnalytics();
    } catch (err) {
      showSnackbar(err.message || "Failed to save lead", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/leads/${id}`, "DELETE");
      showSnackbar("Lead deleted");
      fetchLeads();
      fetchAnalytics();
    } catch (err) {
      showSnackbar(err.message || "Delete failed", "error");
    }
  };

  // Instant, optimistic status update — no modal, no page reload.
  const handleStatusChange = async (lead, newStatus) => {
    if (newStatus === lead.status) return;
    const previous = lead.status;
    setUpdatingStatusId(lead.id);
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: newStatus } : l)));
    try {
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/leads/${lead.id}/status`, "PATCH", {
        status: newStatus,
      });
      showSnackbar(`Status updated to ${STATUS_META[newStatus]?.label || newStatus}`);
      if (statusFilter && statusFilter !== newStatus) {
        setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      }

      fetchAnalytics();
    } catch (err) {
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: previous } : l)));
      showSnackbar(err.message || "Failed to update status", "error");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const toggleSortDir = () => setSortDir((d) => (d === "asc" ? "desc" : "asc"));

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setSortDir("desc");
  };

  const paginatedLeads = leads.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Renders a summary for the table's Product column when a lead has multiple products.
  const productSummary = (lead) => {

    const names = Array.isArray(lead.products) && lead.products.length > 0
      ? lead.products.map((p) => p.productName)
      : lead.productName
        ? [lead.productName]
        : [];
    if (names.length === 0) return "—";
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1} more`;
  };

  const calculateTotalOrderValue = (products) => {
    if (!Array.isArray(products) || products.length === 0) return 0;

    return products.reduce(
      (total, product) =>
        total +
        (Number(product.unitPrice) || 0) * (Number(product.quantity) || 0),
      0
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Lead Block System
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Create Lead
        </Button>
      </Box>

      {/* ── ANALYTICS ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Total Leads
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {analyticsLoading ? <CircularProgress size={20} /> : analytics.total ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {STATUS_ORDER.map((key) => {
          const meta = STATUS_META[key];
          const Icon = meta.icon;
          return (
            <Grid item xs={6} sm={4} md={2} key={key}>
              <Card
                variant="outlined"
                sx={{ cursor: "pointer", borderColor: statusFilter === key ? `${meta.color}.main` : undefined }}
                onClick={() => {
                  setStatusFilter((prev) => (prev === key ? "" : key));
                  setPage(0);
                }}
              >
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Icon fontSize="small" color={meta.color === "default" ? "action" : meta.color} />
                    <Typography variant="caption" color="text.secondary">
                      {meta.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700}>
                    {analyticsLoading ? <CircularProgress size={20} /> : analytics.counts?.[key] ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {analytics.expiringSoon > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<ListAltIcon fontSize="inherit" />}>
          {analytics.expiringSoon} blocked lead{analytics.expiringSoon > 1 ? "s" : ""} expiring within the next 3 days.
        </Alert>
      )}

      {/* ── SEARCH / FILTER TOOLBAR ── */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search reseller, product, brand, client, phone…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_ORDER.map((key) => (
              <MenuItem key={key} value={key}>
                {STATUS_META[key].label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {(search || statusFilter || sortDir !== "desc") && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </Box>

      <Paper sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f1f3f6" }}>
              <TableCell>Lead No / Created By</TableCell>
              <TableCell>End Client</TableCell>
              <TableCell>Reseller</TableCell>
              <TableCell>Phone/ Email</TableCell>
              <TableCell>Product(s)</TableCell>
              <TableCell>Order Value</TableCell>
              <TableCell>Location</TableCell>
              <TableCell sortDirection={sortDir}>
                <TableSortLabel active direction={sortDir} onClick={toggleSortDir}>
                  Blocked Until
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLeads.length > 0 ? (
              paginatedLeads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>{lead.leadNumber}<br></br> <Chip label={lead.createdBy || "-"} size="small" color="info" sx={{ mt: 0.5 }}/></TableCell>
                  <TableCell>{lead.endClientName}</TableCell>
                  <TableCell>{lead.resellerName}</TableCell>
                  <TableCell>{lead.phoneNumber} <br></br> {lead.email}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        Array.isArray(lead.products) ? lead.products.map((p) => p.productName).join(", ") : ""
                      }
                    >
                      <span>{productSummary(lead)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const totalValue = Array.isArray(lead.products)
                        ? lead.products.reduce((sum, p) => sum + (p.quantity || 0) * (p.unitPrice || 0), 0)
                        : (lead.quantity || 0) * (lead.unitPrice || 0);
                      return formatCurrency(totalValue);
                    })()}
                  </TableCell>

                  <TableCell>{lead.location}</TableCell>
                  <TableCell>{toDateInputValue(lead.blockedDate)}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={lead.status || "BLOCK"}
                      disabled={updatingStatusId === lead.id}
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                      renderValue={(value) => <StatusChip status={value} />}
                      sx={{
                        minWidth: 140,
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      {STATUS_ORDER.map((key) => (
                        <MenuItem key={key} value={key}>
                          <StatusChip status={key} />
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center">

                    <IconButton
                      size="small"
                      onClick={() => handleOpenView(lead)}
                      sx={{ color: "#0288d1" }}
                      aria-label="View lead"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(lead)}
                      sx={{ color: "#1976d2" }}
                      aria-label="Edit lead"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>

                      {isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(lead.id)}
                          sx={{ color: "#dc2626" }}
                          aria-label="Delete lead"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}

                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  {loading ? "Loading…" : "No leads found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={leads.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* CREATE / EDIT MODAL */}
      <Dialog open={openModal} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? "Edit Lead" : "Create Lead"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Reseller Name"
            value={form.resellerName}
            onChange={(e) => handleChange("resellerName", e.target.value)}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Created By"
            margin="dense"
            value={username || ""}
            onChange={(e)=> handleChange("createdBy", e.target.value)}
            InputProps={{ readOnly: true, sx: { color: "text.secondary" } }}
          />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Phone Number"
              value={form.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
              error={Boolean(form.phoneNumber && !isValidPhone(form.phoneNumber))}
              helperText={
                form.phoneNumber && !isValidPhone(form.phoneNumber)
                  ? "Enter a valid mobile number"
                  : ""
              }
            />
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={Boolean(form.email && !isValidEmail(form.email))}
              helperText={
                form.email && !isValidEmail(form.email)
                  ? "Enter a valid email address"
                  : ""
              }
            />
          </Box>

          {/* ── PRODUCT LINE ITEMS ── */}
          <Box sx={{ mt: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle2">Products</Typography>
            <Button size="small" startIcon={<SearchIcon />} onClick={() => setProductModalOpen(true)}>
              Add Product
            </Button>
          </Box>

          {form.products.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No products added yet. Click "Add Product" to search the catalog — you can add as many as you need.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Click "Add Product" to add a new product, or use the copy icon to add another color/size of the same one.
              </Typography>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell width={190}>Color</TableCell>
                    <TableCell width={170}>Size</TableCell>
                    <TableCell width={90}>Qty</TableCell>
                    <TableCell width={110}>Unit Price</TableCell>
                    <TableCell width={70} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Track first-occurrence per product so the name/brand only
                    // prints once even if that product's lines aren't adjacent.
                    const seenProductKeys = new Set();

                    return form.products.map((p) => {
                      const groupKey = p.ecomProductId ?? p.productName;
                      const isFirstOfGroup = !seenProductKeys.has(groupKey);
                      seenProductKeys.add(groupKey);
                      const rowBg = productGroupColor[groupKey];

                      const takenSizes = p.productColor
                        ? getTakenSizes(form.products, p.lineId, p.ecomProductId, p.productColor)
                        : [];

                      return (
                        <TableRow key={p.lineId} sx={{ bgcolor: rowBg }}>
                          <TableCell sx={{ borderLeft: rowBg ? "3px solid rgba(0,0,0,0.12)" : undefined }}>
                            {isFirstOfGroup ? (
                              <>
                                <Typography variant="body2" fontWeight={600}>
                                  {p.productName}
                                </Typography>
                                {p.brandName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {p.brandName}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                ↳ another variant
                              </Typography>
                            )}
                          </TableCell>

                          {/* Color: always selectable — a color is only "used up" once
                              every size under it has been taken (see Size column). */}
                          <TableCell>
                            {p.availableColors.length > 0 ? (
                              <FormControl size="small" fullWidth>
                                <Select
                                  displayEmpty
                                  value={p.productColor}
                                  renderValue={(value) =>
                                    value ? (
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <ColorDot color={value} />
                                        {value}
                                      </Box>
                                    ) : (
                                      <Typography component="span" color="text.disabled">
                                        Select
                                      </Typography>
                                    )
                                  }
                                  onChange={(e) => handleLineChange(p.lineId, "productColor", e.target.value)}
                                >
                                  <MenuItem value="">
                                    <em>Select</em>
                                  </MenuItem>
                                  {p.availableColors.map((c) => {
                                    const usedCount = getColorUsageCount(form.products, p.lineId, p.ecomProductId, c);
                                    const allSizesTaken =
                                      p.availableSizes.length > 0 &&
                                      p.availableSizes.every((s) =>
                                        getTakenSizes(form.products, p.lineId, p.ecomProductId, c).includes(s)
                                      );
                                    return (
                                      <MenuItem key={c} value={c} disabled={allSizesTaken}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <ColorDot color={c} />
                                            {c}
                                          </Box>
                                          {usedCount > 0 && (
                                            <Chip
                                              label={allSizesTaken ? "all sizes added" : `${usedCount} added`}
                                              size="small"
                                              variant="outlined"
                                              sx={{ height: 18, fontSize: "0.65rem" }}
                                            />
                                          )}
                                        </Box>
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Color"
                                value={p.productColor}
                                onChange={(e) => handleLineChange(p.lineId, "productColor", e.target.value)}
                                InputProps={{
                                  startAdornment: p.productColor ? (
                                    <InputAdornment position="start">
                                      <ColorDot color={p.productColor} size={16} />
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Size: only sizes already used for THIS SAME color on
                              another line are disabled — a different color always
                              gets a fresh set of sizes to pick from. */}
                          <TableCell>
                            {p.availableSizes.length > 0 ? (
                              <FormControl size="small" fullWidth disabled={!p.productColor}>
                                <Select
                                  displayEmpty
                                  value={p.productSize}
                                  onChange={(e) => handleLineChange(p.lineId, "productSize", e.target.value)}
                                >
                                  <MenuItem value="">
                                    <em>{p.productColor ? "Select" : "Pick a color first"}</em>
                                  </MenuItem>
                                  {p.availableSizes.map((s) => {
                                    const taken = takenSizes.includes(s);
                                    return (
                                      <MenuItem key={s} value={s} disabled={taken}>
                                        {s}
                                        {taken && (
                                          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                                            (added)
                                          </Typography>
                                        )}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                                {p.productColor && takenSizes.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Already added: {takenSizes.join(", ")}
                                  </Typography>
                                )}
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Size"
                                value={p.productSize}
                                onChange={(e) => handleLineChange(p.lineId, "productSize", e.target.value)}
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              fullWidth
                              value={p.quantity}
                              onChange={(e) => handleLineChange(p.lineId, "quantity", e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              fullWidth
                              value={p.unitPrice}
                              onChange={(e) => handleLineChange(p.lineId, "unitPrice", e.target.value)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Add another color/size of this product">
                              <IconButton size="small" onClick={() => handleDuplicateLine(p.lineId)} sx={{ color: "#0288d1" }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove this line">
                              <IconButton size="small" onClick={() => handleRemoveLine(p.lineId)} sx={{ color: "#dc2626" }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </>
          )}

          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="End Client"
            value={form.endClientName}
            onChange={(e) => handleChange("endClientName", e.target.value)}
            required
          />
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Location"
            value={form.location}
            onChange={(e) => handleChange("location", e.target.value)}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              type="date"
              label="Blocked Until"
              InputLabelProps={{ shrink: true }}
              value={form.blockedDate}
              onChange={(e) => handleChange("blockedDate", e.target.value)}
              required
            />
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="form-status-label">Status</InputLabel>
              <Select
                labelId="form-status-label"
                label="Status"
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
              >
                {STATUS_ORDER.map((key) => (
                  <MenuItem key={key} value={key}>
                    {STATUS_META[key].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || !isFormValid()}>
            {submitting ? "Saving…" : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW LEAD MODAL */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="md"
        fullWidth
      >

        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid #eee"
          }}
        >

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >

            <Box>

              <Typography
                variant="h5"
                fontWeight={700}
              >
                {selectedLead?.leadNumber}
                <Chip label={selectedLead?.createdBy || "-"} size="small" color="info" sx={{ mx:1 }} />
              </Typography>

              <Typography
                color="text.secondary"
                variant="body2"
              >
                Lead Details
              </Typography>

            </Box>

            <StatusChip status={selectedLead?.status} />

          </Box>

        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: "#f8fafc",
            p: 3
          }}
        >

          {/* Summary */}

          <Grid container spacing={2} sx={{ mb: 3 }}>

            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption">
                  Blocked Until
                </Typography>

                <Typography fontWeight={600}>
                  {toDateInputValue(selectedLead?.blockedDate)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption">
                  Created
                </Typography>

                <Typography fontWeight={600}>
                  {toDateInputValue(selectedLead?.createdAt)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption">
                  Products
                </Typography>

                <Typography fontWeight={600}>
                  {selectedLead?.products?.length || 0}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption">
                  Reseller
                </Typography>

                <Typography fontWeight={600}>
                  {selectedLead?.resellerName}
                </Typography>
              </Paper>
            </Grid>

          </Grid>

          {/* Reseller */}

          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3
            }}
          >

            <CardContent>

              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
              >
                Reseller Information
              </Typography>

              <Grid container spacing={3}>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    Reseller Name
                  </Typography>

                  <Typography fontWeight={600}>
                    {selectedLead?.resellerName}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    Phone
                  </Typography>

                  <Typography>
                    {selectedLead?.phoneNumber || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    Email
                  </Typography>

                  <Typography>
                    {selectedLead?.email || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    End Client
                  </Typography>

                  <Typography>
                    {selectedLead?.endClientName}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    Location
                  </Typography>

                  <Typography>
                    {selectedLead?.location || "-"}
                  </Typography>
                </Grid>

              </Grid>

            </CardContent>

          </Card>

          {/* Products */}

          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2 }}
          >
            Products
          </Typography>

          {groupedProducts.map((product) => (

            <Card
              key={product.productName}
              sx={{
                mb: 3,
                borderRadius: 3
              }}
            >

              <CardContent>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >

                  <Box>

                    <Typography
                      variant="h6"
                      fontWeight={700}
                    >
                      {product.productName}
                    </Typography>

                    <Typography
                      color="text.secondary"
                    >
                      {product.brandName}
                    </Typography>

                  </Box>

                  <Chip
                    color="primary"
                    label={`${product.variants.length} Variant${product.variants.length > 1 ? "s" : ""}`}
                  />

                </Box>

                {product.variants.map((v, index) => (

                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      "&:last-child": {
                        mb: 0
                      }
                    }}
                  >

                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                    >

                      <Grid item xs={12} md={4}>

                        <Typography
                          variant="caption"
                        >
                          Color
                        </Typography>

                        <Box
                          display="flex"
                          alignItems="center"
                          mt={0.5}
                        >

                          <ColorDot
                            color={v.productColor}
                            size={18}
                          />

                          <Typography ml={1}>
                            {v.productColor}
                          </Typography>

                        </Box>

                      </Grid>

                      <Grid item xs={4} md={2}>
                        <Typography variant="caption">
                          Size
                        </Typography>

                        <Typography fontWeight={600}>
                          {v.productSize}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} md={2}>
                        <Typography variant="caption">
                          Qty
                        </Typography>

                        <Typography fontWeight={600}>
                          {v.quantity}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} md={4}>
                        <Typography variant="caption">
                          Unit Price
                        </Typography>

                        <Typography
                          fontWeight={700}
                          color="success.main"
                        >
                          ₹{v.unitPrice}
                        </Typography>
                      </Grid>

                    </Grid>

                  </Paper>

                ))}

              </CardContent>

            </Card>

          ))}

        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ flexGrow: 1, backdropFilter: "blur(4px)" }}>
            Total Products: {selectedLead?.products?.length || 0}
          </Typography>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, backdropFilter: "blur(4px)", color: "success.main", fontWeight: 600 }}>
            Total Order Value: ₹{calculateTotalOrderValue(selectedLead?.products)}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setViewOpen(false)}
          >
            Close
          </Button>

        </DialogActions>

      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Product catalog search modal — supports multi-select */}
      <ProductSelectorModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSelect={handleCatalogSelect}
        fetchDataFunc={sendAuthenticatedRequest}
        baseUrl={urlconfig.backend_api_base_url}
      />
    </Box>
  );
};

export default LeadBlockManagement;